import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { AppButton, Box, ErrorModal, Inscription, Preloader, SearchInput } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import Permissions from '@/common/permissions';
import { SuccessModal } from '@/components/elements';
import { ContentWrapper, ModalWrapper } from '@/components/wrappers';
import { useHasAccess } from '@/modules/core';
import theme from '@/styles/theme';

import { AccountTileMode } from './AccountTile/AccountTile';
import AccountTile from './AccountTile';
import useLinkAccountQuery from './useLinkAccount.query';
import useLinkedAccountsStore from './useLinkedAccounts.store';
import useLinkedEmployeesQuery from './useLinkedEmployees.query';
import {
  useAutomaticallySearchedAccountsQuery,
  useManuallySearchedAccountsQuery,
} from './useSearchedAccounts.query';

const PERMISSIONS = [{ permission: Permissions.LINK_EMPLOYEE_ACCOUNT }];
const MIN_SEARCH_LENGTH = 3;

export enum LinkPopupMode {
  LINK_ACCOUNT = 'LINK_ACCOUNT',
  PRIMARY_ACCOUNT = 'PRIMARY_ACCOUNT',
}

const ITEM_OF_LIST_CLASS_NAME = 'item-of-list';
const StyledWrapList = styled(Box)<{ space: string }>`
  > .${ITEM_OF_LIST_CLASS_NAME}:not(:last-child) {
    margin-bottom: ${({ space }) => space};
  }
`;

const getListTitle = (title: string) => (
  <Inscription
    color="textBody"
    lineHeight="20px"
    weight="bold"
    margin={{ bottom: 'spacing8' }}
  >
    {title}
  </Inscription>
);

interface Props {
  isEditMode?: boolean;
  title?: string;
  isSectionHidden?: boolean;
  externalPopupMode?: LinkPopupMode;
  onPopupClose?: () => void;
  changeOnlyPrimary?: {
    description?: string;
    onClose?: () => void;
    onSave?: (primaryEmployeeId: string) => void;
  };
}

const LinkedAccounts: React.FC<Props> = ({
  isEditMode,
  title = 'Linked Accounts',
  isSectionHidden,
  externalPopupMode,
  onPopupClose,
  changeOnlyPrimary,
}) => {
  const hasAccess = useHasAccess(PERMISSIONS);
  const { params: { id } } = useRouteMatch<{ id: string }>();
  const editableEmployeeId = useMemo(() => hasAccess ? id : '', [id, hasAccess]);
  const [linkPopupMode, setLinkPopupMode] = useState<LinkPopupMode | undefined>(changeOnlyPrimary
    ? LinkPopupMode.PRIMARY_ACCOUNT : undefined);
  const [isSuccessModalShown, setIsSuccessModalShown] = useState(false);
  const { handleReset, linkedEmployeeIds, primaryEmployeeId } = useLinkedAccountsStore();
  const [searchString, setSearchString] = useState('');
  const onCancel = useCallback(() => {
    handleReset();
    setLinkPopupMode(undefined);
    setSearchString('');
    if (changeOnlyPrimary?.onClose) {
      changeOnlyPrimary.onClose();
    }
    if (onPopupClose) {
      onPopupClose();
    }
  }, [handleReset, setLinkPopupMode, setSearchString, changeOnlyPrimary, onPopupClose]);

  useEffect(() => {
    if (externalPopupMode) {
      setLinkPopupMode(externalPopupMode);
    }
  }, [externalPopupMode]);
  const onSuccessLink = useCallback(() => {
    setLinkPopupMode(undefined);
    setIsSuccessModalShown(true);
    if (onPopupClose) {
      onPopupClose();
    }
  }, [setLinkPopupMode, setIsSuccessModalShown, onPopupClose]);
  const [error, setError] = useState('');
  const onErrorLink = useCallback((error: string) => {
    setError(error);
    setLinkPopupMode(undefined);
    if (onPopupClose) {
      onPopupClose();
    }
  }, [setError, onPopupClose]);
  const {
    linkAccount,
    isLoading: isAccountLinking,
  } = useLinkAccountQuery(editableEmployeeId, onSuccessLink, onErrorLink);
  const {
    formattedData: { linkedEmployees },
    isLoading: isGettingLinkedIds,
  } = useLinkedEmployeesQuery(editableEmployeeId);
  const linkedAccounts = useMemo(() => {
    if (changeOnlyPrimary) {
      return linkedEmployees?.filter((item) => !item.isPrimaryLinkedAccount);
    }
    return linkedEmployees;
  }, [linkedEmployees, changeOnlyPrimary]);
  const {
    formattedData: suggestedEmployees,
    isLoading: isAutomaticallySearchedAccountsLoading,
  } = useAutomaticallySearchedAccountsQuery(editableEmployeeId, hasAccess);
  const suggestedAccounts = useMemo(() => suggestedEmployees?.filter((suggestedEmployee) => !linkedAccounts
    ?.some((item) => suggestedEmployee.employeeId === item.employeeId)),
  [linkedAccounts, suggestedEmployees]);
  const handleOpenEmployeePage = useCallback((id?: string) => {
    window.open(`${ROUTES.EMPLOYEE}/${id}`, '_blank');
  }, []);
  const onLinkAccountSubmit = useCallback(() => {
    if (!linkedEmployeeIds?.length) return;

    linkAccount({ linkedEmployeeIds });

  }, [linkedEmployeeIds, linkAccount]);
  const defaultPrimaryEmployeeId = useMemo(() => linkedAccounts
    ?.find((item) => item.isPrimaryLinkedAccount)?.employeeId,
  [linkedAccounts]);
  const onSetPrimary = useCallback(() => {
    if (changeOnlyPrimary?.onSave && primaryEmployeeId) {
      changeOnlyPrimary.onSave(primaryEmployeeId);
      onCancel();
      return;
    }

    if (primaryEmployeeId) linkAccount({ primaryEmployeeId });
  }, [primaryEmployeeId, linkAccount, changeOnlyPrimary, onCancel]);
  const {
    formattedData: manuallySearchedAccounts,
    isLoading: isSearchedAccountsLoading,
  } = useManuallySearchedAccountsQuery(
    editableEmployeeId,
    searchString.length < MIN_SEARCH_LENGTH ? undefined : searchString,
    hasAccess,
  );
  const searchedAccounts = useMemo(() => manuallySearchedAccounts
    ?.filter((manuallySearchedAccount) => {
      const isOnLinkedAccounts = linkedAccounts
        ?.some((item) => item?.employeeId === manuallySearchedAccount?.employeeId);
      const isOnSuggestedAccounts = suggestedAccounts
        ?.some((item) => item?.employeeId === manuallySearchedAccount?.employeeId);
      return !isOnLinkedAccounts && !isOnSuggestedAccounts;
    }) || [],
  [suggestedAccounts, manuallySearchedAccounts, linkedAccounts]);
  const isDataLoading = useMemo(() => isAccountLinking
    || isGettingLinkedIds
    || isSearchedAccountsLoading
    || isAutomaticallySearchedAccountsLoading,
  [
    isAccountLinking,
    isGettingLinkedIds,
    isAutomaticallySearchedAccountsLoading,
    isSearchedAccountsLoading,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => handleReset(), []);

  if (!hasAccess) return null;

  return (
    <>
      <ErrorModal
        testId="LinkedAccounts"
        header={primaryEmployeeId ? 'Primary account has not been changed!' : 'Account has not been linked!'}
        helpText={error}
        visible={Boolean(error)}
        onSetVisible={() => {
          setError('');
          handleReset();
          setSearchString('');
        }}
        buttonText="Ok"
      />
      <SuccessModal
        testId="LinkedAccounts"
        visible={isSuccessModalShown}
        header={primaryEmployeeId ? 'Primary account has been changed!' : 'Account has been linked!'}
        buttonText="Got It!"
        buttonStyle={{ width: '200px' }}
        onSetVisible={() => {
          setIsSuccessModalShown(false);
          handleReset();
          setSearchString('');
        }}
      />
      <ModalWrapper
        contentStyle={{ cursor: 'default' }}
        visible={Boolean(linkPopupMode)}
        title="Link Accounts"
        onSetVisible={onCancel}
        testId={`LinkedAccounts_${linkPopupMode}`}
        {...linkPopupMode === LinkPopupMode.LINK_ACCOUNT ? {
          description: (
            <Box margin={{ top: 'spacing8' }} data-testId="LinkedAccounts_popup_description">
              <Inscription
                color="textBody"
                lineHeight="20px"
                textAlign="center"
              >
                Select multiple employer accounts owned by the same employee. Linking the accounts will allow employees to access all their accounts under a single login.
              </Inscription>
            </Box>
          ),
        } : {}}
        {...changeOnlyPrimary?.description ? {
          description: (
            <Box margin={{ top: 'spacing8' }} data-testId="LinkedAccounts_popup_description">
              <Inscription
                color="textBody"
                lineHeight="20px"
                textAlign="center"
              >
                {changeOnlyPrimary?.description}
              </Inscription>
            </Box>
          ),
        } : null}
      >
        <ContentWrapper>
          <StyledWrapList data-testId="LinkedAccounts_list" space={theme.spacings.spacing12}>
            {linkPopupMode === LinkPopupMode.LINK_ACCOUNT
              ? getListTitle(`Linked account${linkedAccounts?.length && linkedAccounts.length > 1 ? 's' : ''}: ${linkedAccounts?.length || 0}`)
              : null}
            {linkedAccounts?.length ? null : (
              <Inscription color="textBody" lineHeight="20px">No accounts are currently linked to this user.</Inscription>
            )}
            {linkedAccounts?.map((linkedAccount) => (
              <Box className={ITEM_OF_LIST_CLASS_NAME} key={linkedAccount?.employeeId}>
                <AccountTile
                  mode={AccountTileMode.LINKED}
                  account={linkedAccount}
                  isEditMode={isEditMode}
                  isPrimaryDisabled={linkPopupMode !== LinkPopupMode.PRIMARY_ACCOUNT}
                />
              </Box>
            ))}
          </StyledWrapList>

          {linkPopupMode === LinkPopupMode.LINK_ACCOUNT ? (
            <>
              <StyledWrapList
                pad={{ top: 'spacing24' }}
                margin={{ top: 'spacing24' }}
                border={{ side: 'top', size: 'small', color: 'border1' }}
                data-testId="SuggestedAccounts_list"
                space={theme.spacings.spacing12}
              >
                {getListTitle(`Suggested: ${suggestedAccounts.length}`)}
                {suggestedAccounts.length ? null : (
                  <Inscription color="textBody" lineHeight="20px">No suggested accounts were found. You can try searching manually.</Inscription>
                )}
                {suggestedAccounts.map((suggestedAccount) => (
                  <Box
                    className={ITEM_OF_LIST_CLASS_NAME}
                    key={suggestedAccount?.employeeId}
                  >
                    <AccountTile
                      account={suggestedAccount}
                      isEditMode={isEditMode}
                      {...suggestedAccount?.linkedEmployees?.length ? {
                        errorMessage: 'These accounts are interconnected. You can link all of them or none.',
                      } : {}}
                    />
                    {suggestedAccount?.linkedEmployees?.length ? (
                      <Box data-testId={`suggested-already-linked-accounts_${suggestedAccount.employeeId}`}>
                        {
                          suggestedAccount?.linkedEmployees
                            ?.map((linkedEmployee) => <AccountTile key={linkedEmployee?.employeeId} mode={AccountTileMode.GROUPED_ACCOUNT} account={linkedEmployee} isEditMode={isEditMode} />)
                        }
                      </Box>
                    ) : null}
                  </Box>
                ))}
              </StyledWrapList>

              <StyledWrapList
                pad={{ top: 'spacing24' }}
                margin={{ top: 'spacing24' }}
                border={{ side: 'top', size: 'small', color: 'border1' }}
                data-testId="SearchedAccounts_list"
                space={theme.spacings.spacing12}
              >
                <Box
                  data-testId="LinkedAccounts_manualSearch"
                  height="40px"
                  direction="row"
                  justify="between"
                  align="center"
                  {...suggestedAccounts.length ? {
                    margin: { bottom: 'spacing12' },
                  } : {}}
                >
                  <Inscription
                    color="textBody"
                    lineHeight="20px"
                    weight="bold"
                  >
                    {`Manual search${searchedAccounts.length < MIN_SEARCH_LENGTH ? '' : `: ${searchedAccounts.length}`}`}
                  </Inscription>
                  <Box width="364px">
                    <SearchInput
                      testId="LinkedAccounts_manualSearch"
                      value={searchString}
                      hasSearchingHistory
                      placeholder="Search for full name, SSN, EID, PID"
                      onChange={setSearchString}
                      resetValue={searchString}
                      {...searchString && searchString.length < MIN_SEARCH_LENGTH ? {
                        errorText: 'Search starts after entering at least three characters.',
                        error: true,
                      } : {}}
                    />
                  </Box>
                </Box>
                {isSearchedAccountsLoading ? <Box margin={{ top: 'spacing24' }}><Preloader /></Box> : null}
                {searchedAccounts.length || !searchString || isSearchedAccountsLoading ? null : (
                  <Inscription
                    color="textBody"
                    lineHeight="20px"
                    margin={{ top: 'spacing12' }}
                  >
                    No accounts were found matching your search criteria.
                  </Inscription>
                )}
                {!searchString || isSearchedAccountsLoading ? null : searchedAccounts.map((searchedAccount) => (
                  <Box
                    className={ITEM_OF_LIST_CLASS_NAME}
                    key={searchedAccount?.employeeId}
                  >
                    <AccountTile
                      mode={AccountTileMode.MANUAL_SEARCH}
                      account={searchedAccount}
                      isEditMode={isEditMode}
                      {...searchedAccount?.linkedEmployees?.length ? {
                        errorMessage: 'These accounts are interconnected. You can link all of them or none.',
                      } : {}}
                    />
                    {searchedAccount?.linkedEmployees?.length ? (
                      <Box data-testId={`manuallySearched-already-linked-accounts_${searchedAccount.employeeId}`}>
                        {
                          searchedAccount?.linkedEmployees
                            ?.map((linkedEmployee) => <AccountTile key={linkedEmployee?.employeeId} mode={AccountTileMode.GROUPED_ACCOUNT} account={linkedEmployee} isEditMode={isEditMode} />)
                        }
                      </Box>
                    ) : null}
                  </Box>
                ))}
              </StyledWrapList>
            </>
          ) : null}
        </ContentWrapper>

        <Box margin={{ top: 'spacing24' }} direction="row" justify="end" align="center">
          {linkPopupMode === LinkPopupMode.PRIMARY_ACCOUNT && isEditMode && !changeOnlyPrimary ? (
            <Box onClick={() => setLinkPopupMode(LinkPopupMode.LINK_ACCOUNT)} flex>
              <Inscription color="textAccent" lineHeight="20px">Link Another Account</Inscription>
            </Box>
          ) : null}

          <Box direction="row" width="312px" justify="between">
            <AppButton
              testId="LinkedAccounts_cancel"
              onClick={onCancel}
              width="150px"
              buttonType="secondary"
            >
              Cancel
            </AppButton>
            <AppButton
              width="150px"
              {...linkPopupMode === LinkPopupMode.PRIMARY_ACCOUNT ? {
                onClick: () => onSetPrimary(),
                children: isDataLoading ? <Preloader color="white" /> : 'Save',
                ...(!isEditMode
                  || isDataLoading
                  || primaryEmployeeId === defaultPrimaryEmployeeId
                  || !primaryEmployeeId ? {
                    disabled: true,
                    testId: 'LinkedAccounts_submit_SetPrimary_disabled',
                  } : {
                    testId: 'LinkedAccounts_submit_SetPrimary',
                  }),
              } : {
                onClick: () => onLinkAccountSubmit(),
                children: isDataLoading ? <Preloader color="white" /> : 'Confirm',
                ...(!isEditMode || isDataLoading || !linkedEmployeeIds?.length ? {
                  disabled: true,
                  testId: 'LinkedAccounts_submit_Link_disabled',
                } : {
                  testId: 'LinkedAccounts_submit_Link',
                }),
              }}
            />
          </Box>
        </Box>
      </ModalWrapper>

      {changeOnlyPrimary || isSectionHidden ? null : (
        <Box
          pad={{ horizontal: 'spacing32', vertical: 'spacing24' }}
          background="canvas"
          round="container1Round"
          data-testId="LinkedAccounts"
        >
          <Box direction="row" align="center">
            <Box width="320px">
              <Inscription
                size="xxlarge"
                lineHeight="28px"
                color="textTitle"
                data-testId="LinkedAccounts_title"
              >
                {title}
              </Inscription>
            </Box>
            <AppButton
              testId="LinkedAccounts"
              style={{ width: '320px' }}
              buttonType="primary"
              onClick={() => setLinkPopupMode(LinkPopupMode.LINK_ACCOUNT)}
              disabled={!isEditMode}
            >
              Link Account
            </AppButton>
          </Box>

          {linkedAccounts?.length ? (
            <Box direction="row" margin={{ top: 'm' }}>
              <Box width="320px" data-testId="LinkedAccounts_hasLinkedAccounts">
                <Inscription weight="bold" lineHeight="20px" color="textBody">Linked accounts</Inscription>
              </Box>

              <Box>
                <StyledWrapList space={theme.spacings.spacing8}>
                  {linkedAccounts.map((item) => (
                    <Box data-testId={`linkedAccount_${item?.employeeId}`} direction="row" align="center" key={item?.employeeId} className={ITEM_OF_LIST_CLASS_NAME}>
                      <Box onClick={() => handleOpenEmployeePage(item?.employeeId)}>
                        <Inscription lineHeight="20px" color="textAccent">{item?.employerName}</Inscription>
                      </Box>
                      {item?.isPrimaryLinkedAccount ? (
                        <Inscription
                          style={{ marginLeft: theme.spacings.spacing4 }}
                          color="textBody"
                          lineHeight="20px"
                        >
                          (primary account)
                        </Inscription>
                      ) : null}
                    </Box>
                  ))}
                  <Box onClick={() => setLinkPopupMode(LinkPopupMode.PRIMARY_ACCOUNT)}>
                    <Inscription lineHeight="20px" color="textAccent">Show Details</Inscription>
                  </Box>
                </StyledWrapList>
              </Box>
            </Box>
          ) : null}
        </Box>
      )}
    </>
  );
};

export default LinkedAccounts;
